// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — Minerium3D.jsx   ·   YOUR HOLDING, IN THREE DIMENSIONS
// ────────────────────────────────────────────────────────────────────
// ⭐ THIS IS A RENDERER, NOT A GAME.
//
// It takes the holding out of `minerium.js` — props, growers, sky,
// ground, companion — and paints it. Every rule, every timer, every
// write and every price lives in that file and in the shop; this one
// draws what it is handed and has no opinion about any of it. Same
// division `Pier3D.jsx` has with `UnstablePierre.js`, for the same
// reason: a renderer that knows the rules is a renderer you cannot
// replace.
//
// ── THE FOUR LAWS INHERITED FROM Pier3D, NOT ONE OF THEM OPTIONAL ───
//
//  1 · ⚠⚠ FIXED LIGHT COUNT. THREE. FOREVER. "State is sold with
//      emissiveIntensity and basic materials, never by adding one."
//      Every lantern, torch, star lamp and Chao pool in this scene
//      glows without a single pointLight, because a dynamic light
//      multiplies every material's cost SCENE-WIDE and a decorated
//      yard would have thirty of them.
//
//  2 · ⚠ PLAIN BILLBOARD `<Html>` ONLY. drei's `<Html transform>`
//      renders NOTHING on caP'n's machine — proven twice, cost two
//      sessions. Do not "upgrade" it.
//
//  3 · ⚠ THE DOM BUDGET IS THE REAL CONSTRAINT. Every `<Html>` is a
//      real DOM node whose transform is rewritten every frame on the
//      main thread. LABEL_RADIUS below is the dial, and it is the
//      FIRST thing to turn if the yard ever feels grainy — never the
//      geometry, of which there is very little.
//
//  4 · ⚠ REACT CONTEXT DOES NOT CROSS THE `<Canvas>` BOUNDARY. The
//      canvas runs its own reconciler. Everything shared with the
//      outside goes through the `pierCamera` module, never a Provider,
//      and every live value arrives by REF and is read in useFrame.
//
// ── ⚠ AND THE ONE THAT COSTS A SESSION IF FORGOTTEN ────────────────
// A backgrounded browser tab stops calling requestAnimationFrame
// entirely, so a three.js scene issues ZERO draw calls and looks
// EXACTLY like a dead scene. On any "the 3D screen is blank" report
// from a driven browser, check `document.visibilityState` FIRST.
// "hidden" means the loop is paused and there is no bug. Stop looking.
// (PIERRE_PRIME §2, found after ten minutes debugging nothing.)
//
// ── THE BACKDROP ────────────────────────────────────────────────────
// THE UNSTABLE WORLD is the standing horizon for every IE title, and a
// Minerium is a patch of ground inside it. The six elements — cracked
// eclipses, black holes red or violet, shattered pocket dimensions,
// oceans of Chao or Anima, radiating Lyt and Lyf, and THE RISE — are
// what is out there when nobody has said otherwise. Which of them you
// get is what a SKY is: one purchase, one field, a completely different
// place to stand. Zero new lights, `fog={false}` on everything out
// there, one useFrame per system, every element measured against the
// dome so nothing pokes through it.
// ════════════════════════════════════════════════════════════════════

import React, { useRef, useMemo, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";
import { mulberry32, canvasTex, TEX_CACHE as BEING_TEX } from "./ieBeingModels";
import { getPlayerSprite, getIdleSprite, PLAYER_SPRITES } from "./SpriteManifest";
import { MineriumProp, woodMat, releasePropMaterials } from "./mineriumProps";
// ⭐⭐ THE SAME HORIZON THE BOARDWALK STANDS UNDER.
// The yard had forked its own — four hand-built skies, none of which had
// the shattered pocket dimensions or the radiating Lyt/Lyf columns at
// all, and with THE RISE (which the doctrine says is simply out there,
// always) turned into a ✹90,000 cosmetic. A player walked off a pier
// hanging over a living ocean of light, through one door, and stood in a
// field under a poster with two circles on it. That is the single
// biggest reason the Minerium did not read as the same game.
// ⚑ It costs nothing to share: every element is `meshBasicMaterial`
// with `fog={false}` and it adds ZERO lights.
// ⚑ `SKY_RECIPES` still runs — it tints the dome and the eclipse, so the
// four purchasable skies now differ by COLOUR rather than by being four
// different fidelities of the same idea.
// ⚑ A plain one-way import since 2026-09-13: Pier3D no longer imports
// art back out of UnstablePierre, so there is no module cycle here any
// more (the old warning about hoisted declarations is retired). Lifting
// the horizon into its own `unstableWorld.jsx` is still the tidier home.
import { UnstableWorld3D, CameraBar, useHdSky } from "./Pier3D";
import { SKY_RECIPES, skyDomeTex } from "./ieSkies";
import { Companion } from "./mineriumCompanion";
import { Manor } from "./mineriumManor";
import { releaseTextures } from "./pierTex";
import { ITEMS, KIND, AREAS, sizeOf } from "./mineriumCatalogue";
import { UI } from "./theme";
import { YARD, resolvePlacement, growthOf, serverNow, propsInArea } from "./minerium";
import { burstStyle, GHOST_VERB } from "./buildKit";
import {
  getView, viewMult, isPosed, setPosed, resetToken, setGroundForward, resetGroundForward, subscribe,
} from "./pierCamera";

// ⭐ THE ONE SCALE, taken from Pier3D unchanged. A player who is 1.5
// tiles tall on the boardwalk is 1.5 tiles tall in their own yard, or
// the two stop reading as one world.
const ART_TILE = 48;
const DF_TILE  = 12;

// ⭐ THE DOM BUDGET DIAL (law 3 above).
const LABEL_RADIUS = 14;

// true once real idle frames exist again (they 404 today — SpriteManifest)
const IDLE_ART = PLAYER_SPRITES.player_f_idle_a !== PLAYER_SPRITES.player_f_down_a;

// THE CANON COLOURS, the same eight names the pier uses.
const C = {
  nullus: "#B388FF", omnis: "#F2C14E", anima: "#FF4D8D", chao: "#7CE7FF",
  lyt: "#FFE9A0", lyf: "#7FE8D8", ruin: "#E8453C", shade: "#2A1540",
};

const CX = (YARD.x0 + YARD.x1) / 2;
const CZ = (YARD.y0 + YARD.y1) / 2;
const RAD = Math.max(YARD.x1 - YARD.x0, YARD.y1 - YARD.y0) / 2;
const DOME = Math.max(240, 3.4 * RAD * 2.2);

// ════════════════════════════════════════════════════════════════════
// THE SPRITE — the player, and only the player
// ────────────────────────────────────────────────────────────────────
// ⚠ terror-assets sends no CORS headers, and a cross-origin image
// WITHOUT CORS cannot legally become a WebGL texture — it throws. The
// player sprite could never have been a mesh. Billboard <Html> was
// always the only road, and that is why the actor below is DOM while
// everything around it is geometry.
// ════════════════════════════════════════════════════════════════════
function SpriteImg({ src, size, glow }) {
  // ⚠ SILENCE BEATS A BROKEN BOX — the pier learned this on 2026-08-25 and
  // the yard never did: a sprite that 404s drew the browser's broken-image
  // icon standing in the middle of somebody's farm.
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);
  if (!src || failed) return null;
  return (
    <img src={src} alt="" width={size} height={size} draggable={false} onError={() => setFailed(true)}
      style={{ imageRendering: "pixelated", filter: glow || undefined, userSelect: "none", display: "block" }} />
  );
}

const camRelativeDir = (fx, fz, camYaw) => {
  const a = Math.atan2(fx, fz) - camYaw;
  const q = Math.round(a / (Math.PI / 2)) & 3;
  return [{ x: 0, y: 1 }, { x: -1, y: 0 }, { x: 0, y: -1 }, { x: 1, y: 0 }][q];
};

function YardActor({ posRef, facingRef, gender }) {
  const g = useRef();
  const [look, setLook] = useState({ dx: 0, dy: 1, moving: false, idleB: false });
  const lookRef = useRef(look);
  const lastP = useRef({ ...posRef.current });
  const acc = useRef(0);
  // ⚠ CAPTURED ONCE. r3f re-applies a `position` prop on every render,
  // so pointing it at a value the frame loop mutates would snap the
  // actor back to wherever the last look-change left it.
  const initPos = useRef([posRef.current.x, 0, posRef.current.y]);

  useFrame(({ camera, clock }, dt) => {
    const o = g.current; if (!o) return;
    const p = posRef.current;
    const k = 1 - Math.pow(0.001, Math.min(dt || 0.016, 0.05));
    o.position.x += (p.x - o.position.x) * k;
    o.position.z += (p.y - o.position.z) * k;

    acc.current += dt || 0.016;
    if (acc.current < 0.14) return;                   // re-evaluate at ~7Hz
    const moved = Math.hypot(p.x - lastP.current.x, p.y - lastP.current.y) > 0.004 * (acc.current / 0.016);
    acc.current = 0;
    lastP.current = { x: p.x, y: p.y };

    const camYaw = Math.atan2(camera.position.x - o.position.x, camera.position.z - o.position.z);
    const nd = camRelativeDir(facingRef.current.x || 0, facingRef.current.y || 1, camYaw);
    const idleB = Math.floor(clock.elapsedTime / 1.7) % 2 === 1;
    const cur = lookRef.current;
    if (nd.x !== cur.dx || nd.y !== cur.dy || moved !== cur.moving || (IDLE_ART && !moved && idleB !== cur.idleB)) {
      const next = { dx: nd.x, dy: nd.y, moving: moved, idleB };
      lookRef.current = next;
      setLook(next);
    }
  });

  // ⚠ While the idle art is missing from the CDN (SpriteManifest aliases it
  // to the walk-down frame), standing still keeps the way you were facing —
  // snapping to face the camera every time you stopped read as a glitch.
  const src = look.moving || !IDLE_ART
    ? getPlayerSprite(gender, look.dx, look.dy, false)
    : getIdleSprite(gender, look.idleB);

  return (
    <group ref={g} position={initPos.current}>
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.42, 16]} />
        <meshBasicMaterial color="#000" transparent opacity={0.42} depthWrite={false} />
      </mesh>
      <Html center distanceFactor={DF_TILE} position={[0, 0.78, 0]}
            zIndexRange={[8, 0]} style={{ pointerEvents: "none" }}>
        <SpriteImg src={src} size={ART_TILE * 1.5} glow="drop-shadow(0 0 10px #9ad46a55)" />
      </Html>
    </group>
  );
}

// ⭐ THE OTHERS (2026-09-15). Friends could walk your Minerium since
// OW-23 and could not SEE each other in it — the yard drew exactly one
// actor. Same presence node the Hall uses (`homes/{owner}/players`), one
// `room: "yard"` field apart; the others arrive by ref and are read in
// useFrame, exactly as Hall3D's RemoteActor does it.
function RemoteYardActor({ uid, meta, othersRef }) {
  const posRef = useRef({ x: 0, y: 0 });
  const facingRef = useRef({ x: 0, y: 1 });
  useFrame(() => {
    const d = othersRef.current?.[uid]; if (!d) return;
    posRef.current.x = Number(d.x) || 0; posRef.current.y = Number(d.y) || 0;
    facingRef.current.x = Number(d.facingX) || 0; facingRef.current.y = Number(d.facingY) || 0;
  });
  return (
    <group>
      <YardActor posRef={posRef} facingRef={facingRef} gender={meta.gender} />
      <NameTag posRef={posRef} text={meta.name} />
    </group>
  );
}
function NameTag({ posRef, text }) {
  const g = useRef();
  const init = useRef([posRef.current.x, 1.72, posRef.current.y]);
  useFrame(() => { const o = g.current; if (!o) return; o.position.x = posRef.current.x; o.position.z = posRef.current.y; });
  if (!text) return null;
  return (
    <group ref={g} position={init.current}>
      <Html center distanceFactor={DF_TILE} zIndexRange={[7, 0]} style={{ pointerEvents: "none" }}>
        <div style={{ fontFamily: UI.font, fontSize: 11, fontWeight: 700, letterSpacing: ".12em", color: "#a78bfa",
                      textShadow: "0 0 8px #000, 0 1px 2px #000", whiteSpace: "nowrap" }}>{String(text).toUpperCase()}</div>
      </Html>
    </group>
  );
}

// ⭐ THE SKY RECIPES MOVED to `ieSkies.jsx` (2026-09-25) — one sky, worn
// in the yard AND on the pier (and any future title). Imported below.
// ⚠ `CrackedEclipse`, `BlackHole` and `TheRise` LIVED HERE AND ARE GONE.
// They were this file's private, thinner re-implementations of three of
// the six standing elements of the Unstable World. The yard now mounts
// the real horizon (`UnstableWorld3D`, from Pier3D), which draws all six,
// so keeping these meant drawing two eclipses and two Rises in the same
// sky. ⚑ Deleted rather than commented out: an unreachable component is
// a build error under CI=true, and a second copy of a doctrine element
// is exactly what THE COHERENCY LAW exists to prevent.
function MineriumSky({ skyId }) {
  const spec = SKY_RECIPES[skyId] || SKY_RECIPES.sky_default;
  const tex = useMemo(() => skyDomeTex(SKY_RECIPES[skyId] ? skyId : "sky_default", spec), [skyId, spec]);
  // ⭐ a remastered sky brings its Blender dome; the painting stands in until it lands
  const hd = useHdSky(spec.hd || null);


  return (
    <group>
      <mesh position={[CX, 0, CZ]}>
        <sphereGeometry args={[DOME, 32, 20]} />
        <meshBasicMaterial map={hd?.dome || tex} side={THREE.BackSide} fog={false} toneMapped={false} depthWrite={false} />
      </mesh>
      {/* ⚠⚠ THE ECLIPSE, THE BLACK HOLES, THE SEA AND THE RISE ARE GONE
          FROM HERE, and that is the fix rather than a loss. `Scene` now
          mounts `UnstableWorld3D` — the pier's own horizon — which draws
          all four AND the two elements this file never had (the shattered
          pocket dimensions and the Lyt/Lyf columns). Left in, the yard
          rendered TWO cracked eclipses in the same quadrant: a 9.5°-wide
          one at 230 units from this file and a 38°-wide one at 45 units
          from the shared horizon, the near one occluding the far one.
          Same doubling for the holes and for THE RISE.
          ⛑ A purchasable sky is now a TINT AND A DOME — which is what it
          should always have been, and is why the four of them stop being
          four different fidelities of the same idea. `spec.sea`,
          `spec.rise` and `spec.holes` stay in the recipes; they colour
          the dome and are read by the audit. */}
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE GROUND AND THE FENCE
// ════════════════════════════════════════════════════════════════════
// ⭐ (2026-09-16) each ground is its own SURFACE, not a recolour:
//   grass — tufts and a scatter of small flowers
//   dust  — the ground-down pocket dimension: a network of plate cracks,
//           and shards of it glinting (the glints are the emissive map)
//   moss  — lumpy cushions of moss and spores that glow Lyf-teal
export const GROUND_RECIPES = {
  // ⚠ lifted: at #2c3a22 the lawn read as near-black under the pier's night light
  ground_grass:      { base: "#3c4e2e", hi: "#739c52", lo: "#20301a", tuft: "#92bd66", motes: null, flowers: ["#ff8ac4", "#ffd24a", "#8ad8ff", "#e8e0ff"] },
  ground_dust:       { base: "#3a3140", hi: "#6a5a70", lo: "#1a1420", tuft: null,      motes: "#c9b4ff", plates: "#0e0a14", glints: "#d9c8ff", glow: "#b388ff" },
  ground_anima_moss: { base: "#16302c", hi: "#3e7a6a", lo: "#0a1816", tuft: null,      motes: "#7fe8d8", cushions: "#2e6a5a", spores: "#7fe8d8", glow: "#7fe8d8" },
};

// the glowing half of a ground (glints / spores), same seed as its map
function groundGlowTex(id, spec) {
  if (!spec.glow) return null;
  return canvasTex(`min_ground_glow_v2_${id}`, 512, 512, (ctx, S) => {
    const R = mulberry32(0x6A0 ^ id.length * 977);      // its own seed — the glints need not sit on the map's specks
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, S, S);
    if (spec.glints) {
      for (let i = 0; i < 70; i++) {                        // shard glints
        const x = R() * S, y = R() * S, r = 1.5 + R() * 3.5;
        ctx.fillStyle = spec.glints;
        ctx.beginPath(); ctx.moveTo(x, y - r * 1.6); ctx.lineTo(x + r, y); ctx.lineTo(x, y + r * 1.6); ctx.lineTo(x - r, y); ctx.fill();
      }
    }
    if (spores(spec)) {
      for (let i = 0; i < 260; i++) {                       // spores, clustered
        const cx = R() * S, cy = R() * S;
        for (let k = 0; k < 4; k++) {
          const g = ctx.createRadialGradient(cx + (R() - 0.5) * 14, cy + (R() - 0.5) * 14, 0, cx, cy, 3 + R() * 3);
          g.addColorStop(0, spec.spores); g.addColorStop(1, "#000000");
          ctx.fillStyle = g; ctx.fillRect(cx - 8, cy - 8, 16, 16);
        }
      }
    }
  });
}
const spores = (spec) => !!spec.spores;

export function groundTex(id, spec) {
  return canvasTex(`min_ground_v2_${id}`, 512, 512, (ctx, S) => {
    const R = mulberry32(0xC0FFEE ^ id.length * 131);
    ctx.fillStyle = spec.base; ctx.fillRect(0, 0, S, S);
    for (let i = 0; i < 2600; i++) {
      ctx.globalAlpha = 0.04 + R() * 0.2;
      ctx.fillStyle = R() > 0.62 ? spec.hi : spec.lo;
      const s = 1 + R() * 5;
      ctx.fillRect(R() * S, R() * S, s, s);
    }
    if (spec.tuft) {
      for (let i = 0; i < 420; i++) {
        ctx.globalAlpha = 0.18 + R() * 0.4;
        ctx.strokeStyle = spec.tuft;
        ctx.lineWidth = 0.8 + R();
        const x = R() * S, y = R() * S, h = 4 + R() * 11;
        ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + (R() - 0.5) * 5, y - h); ctx.stroke();
      }
    }
    if (spec.flowers) {                                   // small flowers in the lawn
      for (let i = 0; i < 90; i++) {
        ctx.globalAlpha = 0.7 + R() * 0.3;
        ctx.fillStyle = spec.flowers[i % spec.flowers.length];
        const x = R() * S, y = R() * S;
        for (let k = 0; k < 5; k++) { const a = k * 1.2566; ctx.beginPath(); ctx.arc(x + Math.cos(a) * 2.2, y + Math.sin(a) * 2.2, 1.6, 0, 7); ctx.fill(); }
        ctx.fillStyle = "#fff4c0"; ctx.beginPath(); ctx.arc(x, y, 1.1, 0, 7); ctx.fill();
      }
    }
    if (spec.plates) {                                    // the plate-crack network
      ctx.globalAlpha = 0.85; ctx.strokeStyle = spec.plates;
      for (let i = 0; i < 46; i++) {
        ctx.lineWidth = 1 + R() * 2.2;
        ctx.beginPath(); let x = R() * S, y = R() * S; ctx.moveTo(x, y);
        for (let k = 0; k < 5; k++) { x += (R() - 0.5) * 90; y += (R() - 0.5) * 90; ctx.lineTo(x, y); }
        ctx.stroke();
      }
      ctx.globalAlpha = 0.35; ctx.fillStyle = spec.hi;     // the lit edge of each plate
      for (let i = 0; i < 300; i++) ctx.fillRect(R() * S, R() * S, 3 + R() * 6, 1);
    }
    if (spec.cushions) {                                  // moss cushions, lumpy
      for (let i = 0; i < 160; i++) {
        const x = R() * S, y = R() * S, r = 6 + R() * 18;
        const g = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, 1, x, y, r);
        g.addColorStop(0, spec.hi); g.addColorStop(0.7, spec.cushions); g.addColorStop(1, spec.cushions + "00");
        ctx.globalAlpha = 0.55 + R() * 0.35; ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  });
}

function Yard({ groundId, tapRef }) {
  const spec = GROUND_RECIPES[groundId] || GROUND_RECIPES.ground_grass;
  const key = GROUND_RECIPES[groundId] ? groundId : "ground_grass";
  const tex = useMemo(() => {
    const t = groundTex(key, spec);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(6, 5);
    return t;
  }, [key, spec]);
  const glowTex = useMemo(() => {
    const t = groundGlowTex(key, spec);
    if (t) { t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(6, 5); }
    return t;
  }, [key, spec]);

  const w = YARD.x1 - YARD.x0 + 2;
  const d = YARD.y1 - YARD.y0 + 2;

  // ⚑ ONE HOOK FOR THE WHOLE CROWD. Twenty-four motes on twenty-four
  // useFrames is twenty-four callbacks a frame for a result that reads
  // identically to one group turning.
  const motes = useRef();
  useFrame(({ clock }) => {
    if (motes.current) {
      const t = clock.elapsedTime;
      motes.current.rotation.y = t * 0.03;
      motes.current.position.y = 0.9 + Math.sin(t * 0.4) * 0.14;
    }
  });
  const motePts = useMemo(() => {
    if (!spec.motes) return [];
    const R = mulberry32(0x4471);
    return Array.from({ length: 22 }, () => ({
      x: (R() - 0.5) * w * 0.9, y: (R() - 0.5) * 1.6, z: (R() - 0.5) * d * 0.9, s: 0.018 + R() * 0.03,
    }));
  }, [spec.motes, w, d]);

  return (
    <group>
      {/* the ground runs on under the house, so the manor stands on the
          holding rather than on the translucent apron over the sea */}
      {/* ⭐ TAP THE GROUND to aim a held piece (2026-09-15). `e.point` is
          in world units, which ARE yard tiles here. */}
      <mesh position={[CX, 0, CZ - 3.5]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow={false}
            onClick={(e) => { if (e.delta > 3) return; tapRef?.current?.ground?.(e.point.x, e.point.z); }}>
        <planeGeometry args={[w, d + 7]} />
        {/* keyed on the ground so a map/emissive swap is a fresh material */}
        <meshStandardMaterial key={key} map={tex} roughness={1} metalness={0}
                              emissiveMap={glowTex || null} emissive={spec.glow || "#000000"} emissiveIntensity={glowTex ? 0.9 : 0} />
      </mesh>
      {/* The apron — the ground does not simply stop at the fence.
          ⚠ IT USED TO BE 2.6× THE YARD AT 85% OPACITY, which is a 65×49
          sheet that painted straight over the shared horizon's ocean of
          Chao and cut the bases off its Lyt/Lyf columns. The point of an
          apron is that the ground fades out, not that it hides what is
          beyond it — so it is smaller now and it actually fades. */}
      <mesh position={[CX, -0.06, CZ]} rotation={[-Math.PI / 2, 0, 0]}>
        {/* just past the fence — a lighter sheet reaching out over the sea read as a glitch */}
        <planeGeometry args={[w * 1.12, d * 1.12]} />
        <meshBasicMaterial color={spec.lo} transparent opacity={0.55} fog={false} depthWrite={false} />
      </mesh>
      {spec.motes && (
        <group ref={motes} position={[CX, 0.9, CZ]}>
          {motePts.map((m, i) => (
            <mesh key={i} position={[m.x, m.y, m.z]}>
              <sphereGeometry args={[m.s, 6, 5]} />
              <meshBasicMaterial color={spec.motes} transparent opacity={0.5} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </group>
      )}
    </group>
  );
}

// ── THE HOUSE ───────────────────────────────────────────────────────
// ⚑ THE ARCHWAY IS PERMANENT AND IT IS NOT A PLACEABLE. caP'n asked for
// doors you can put anywhere and buy prettier ones — and the failure
// mode that creates is real: put your only door somewhere silly, pick
// it up, and you are locked out of your own Hall. So the house keeps
// one archway that can never be removed, and every door you PLACE is an
// extra entrance and a nicer one. You cannot lock yourself out.
export const HOUSE_DOOR = { x: CX, y: YARD.y0 - 0.4 };

// ⚠ 1.6 tiles apart with no rails is not a fence, it is a row of stakes.
// The purchasable Fence Post next door carries two rails "so one post
// still reads as fencing" — the PERMANENT boundary was cheaper than the
// thing you can buy to stand beside it.
// ⚠ MODULE SCOPE, not inside the memo: the rails read it from the JSX,
// which is a different scope entirely. It was declared in the callback
// and used in the return — a ReferenceError on Fence's first render.
// ⚠ 1.6, NOT 1.0. Dropping it to 1.0 took the run from 40 posts to 63,
// and with a cap on each that is 126 meshes plus rails — 3.3× the draws,
// on a scene whose own header says it must run on a phone, for a fence.
// The rails are what made it read as a boundary; the spacing never was.
const FENCE_STEP = 1.6;

function Fence() {
  // The boundary, drawn as thin posts with CONTINUOUS rails. It is
  // scenery, not collision — `inYard()` in minerium.js owns that, and two
  // sources of truth about where the edge is would drift by Tuesday.
  //
  // ⚠ RAILS ARE PER-RUN, NOT PER-POST. Two rails on every post is four
  // meshes each, and at this spacing that is ~250 separate draws on a
  // screen whose own header says it has to run on a phone — for a fence.
  // Three runs × two rails is six meshes and it also looks better,
  // because a real rail is one length of timber and not a row of stubs.
  const { posts, runs } = useMemo(() => {
    const p = [], r = [];
    const x0 = YARD.x0 - 0.6, x1 = YARD.x1 + 0.6, z1 = YARD.y1 + 0.6, z0 = YARD.y0 - 0.5;
    for (let x = x0; x <= x1 + 0.001; x += FENCE_STEP) p.push([x, z1]);
    for (let z = z0; z <= z1 + 0.001; z += FENCE_STEP) { p.push([x0, z]); p.push([x1, z]); }
    // [centreX, centreZ, length, yaw]
    r.push([(x0 + x1) / 2, z1, x1 - x0, 0]);
    r.push([x0, (z0 + z1) / 2, z1 - z0, Math.PI / 2]);
    r.push([x1, (z0 + z1) / 2, z1 - z0, Math.PI / 2]);
    return { posts: p, runs: r };
  }, []);

  return (
    <group>
      {posts.map(([x, z], i) => (
        <group key={`p${i}`} position={[x, 0, z]}>
          {/* painted oak — the same material every buyable wooden thing
              in the yard uses, not a flat brown cylinder */}
          <mesh position={[0, 0.42, 0]} material={woodMat()}>
            <cylinderGeometry args={[0.055, 0.075, 0.84, 7]} />
          </mesh>
        </group>
      ))}
      {runs.map(([x, z, len, yaw], i) => (
        <group key={`r${i}`} position={[x, 0, z]} rotation={[0, yaw, 0]}>
          {[0.30, 0.60].map((ry) => (
            <mesh key={ry} position={[0, ry, 0]} material={woodMat()}>
              <boxGeometry args={[len, 0.055, 0.045]} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE PLACED THINGS
// ════════════════════════════════════════════════════════════════════
// ⚠ React.memo per prop, keyed on the values that actually change its
// picture. A yard with forty props inside a screen that ticks a HUD
// would otherwise reconcile forty subtrees a second for nothing.
// ⭐ OW-34 · a rectangle's outline, drawn in the piece's OWN frame (w along
// x, d along z, unturned), so it turns with the piece that owns it.
function RectRing({ w, d, color, opacity = 0.85, y = 0.03 }) {
  const t = 0.07, hx = w / 2, hz = d / 2;
  return (
    <group position={[0, y, 0]}>
      {[[0, -hz, w + t, t], [0, hz, w + t, t], [-hx, 0, t, d], [hx, 0, t, d]].map(([x, z, bw, bd], i) => (
        <mesh key={i} position={[x, 0, z]}>
          <boxGeometry args={[bw, 0.012, bd]} />
          <meshBasicMaterial color={color} transparent opacity={opacity} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

const Placed = React.memo(function Placed({ propId, itemId, x, z, rot, lift, fill, ripe, selected, tapRef, hidden }) {
  const [sw, sd] = sizeOf(itemId);
  // ⭐ CLICK TO SELECT (2026-09-15). r3f fires onClick only when the
  // pointer did not drag, so an orbit never counts as a tap.
  // ⚠ `e.delta` is the pointer's travel since pointerdown — r3f fires
  // onClick after an orbit DRAG too, so an orbit that ended over a chair
  // used to select it (review, 2026-09-15). And a hidden group still
  // raycasts, so the handler comes off while the piece is being moved.
  const onClick = (e) => { if (e.delta > 3) return; e.stopPropagation(); tapRef?.current?.prop?.(propId); };
  return (
    <group position={[x, lift, z]} rotation={[0, rot || 0, 0]} visible={!hidden} onClick={hidden ? undefined : onClick}>
      <MineriumProp itemId={itemId} fill={fill} ripe={ripe} />
      {/* ⚠ THE ASCENSION RING IS [0.52, 0.62] TOO. Standing next to a
          Mythical in build mode drew two identical concentric rings a
          centimetre apart in two different colours, which reads as a
          rendering fault rather than as a selection. Moved out. */}
      {selected && sw === sd && (
        <mesh position={[0, 0.03 - lift, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.88 * Math.max(1, sw * 0.62), 0.98 * Math.max(1, sw * 0.62), 30]} />
          <meshBasicMaterial color={UI.ok} transparent opacity={0.85} toneMapped={false} depthWrite={false} />
        </mesh>
      )}
      {selected && sw !== sd && <RectRing w={sw + 0.1} d={sd + 0.1} color={UI.ok} y={0.03 - lift} />}
    </group>
  );
});

// A grower with something to collect gets a mark above it. This is the
// ONLY per-prop <Html>, it is culled to LABEL_RADIUS, and it is the dial
// to turn first if the yard ever grains.
function ReadyMark({ x, z, amount }) {
  const g = useRef();
  // ⚠ the position is set ONCE and then owned by the frame loop — a fresh
  // `position` array on every 4 s tick snapped the bob back to 1.5
  const init = useRef([x, 1.5, z]);
  useFrame(({ clock }) => {
    if (g.current) g.current.position.y = 1.5 + Math.sin(clock.elapsedTime * 2.1) * 0.08;
  });
  return (
    <group position={init.current} ref={g}>
      <Html center distanceFactor={DF_TILE} zIndexRange={[7, 0]} style={{ pointerEvents: "none" }}>
        {/* ⚠ A lime terminal chip floating over a crop bed was the only
            in-world type in the whole yard, and it matched nothing. The
            Dawgs green stays on the NUMBER, because that is a currency
            tint; the chip is the house's. */}
        <div style={{
          fontFamily: UI.font, fontSize: 13, fontWeight: 700,
          color: UI.ok, background: UI.panel, padding: "3px 9px", borderRadius: UI.rSm,
          border: `1px solid ${UI.gold}66`, whiteSpace: "nowrap",
          letterSpacing: UI.track.body,
          boxShadow: `0 0 14px ${UI.ok}44, 0 6px 18px #000a`,
        }}>✹{amount}</div>
      </Html>
    </group>
  );
}

function PropsLayer({ holding, nowRef, selectedId, buildMode, tapRef, movingPropId }) {
  const { props } = holding;
  // Re-derive the drawable list only when the holding itself changes —
  // never on a HUD tick, and never inside useFrame.
  const drawn = useMemo(() => {
    const out = [];
    // ⚠⚠ THE YARD ONLY. This swept the WHOLE holding, and the day the
    // Hall could hold furniture (OW-24) that meant every chair, bench and
    // pillar indoors was also standing in the yard at mirrored
    // coordinates — overlapping the crops, un-collidable (`collides` IS
    // area-aware, so you could plant straight through a phantom
    // fountain), and visible to any guest walking your farm. A renderer
    // that draws the other room is the exact failure the two-tile-space
    // law exists to prevent. Found by adversarial review 2026-09-13.
    for (const p of propsInArea(props, AREAS.YARD)) {
      const item = ITEMS[p.itemId];
      if (!item) continue;
      const at = resolvePlacement(props, p.propId);
      if (!at) continue;
      out.push({ ...p, item, ax: at.x, az: at.y, lift: at.lift, arot: at.rot });
    }
    return out;
  }, [props]);

  // The ripeness readout ticks on its own slow clock — growth is a
  // minutes-scale thing and re-deriving it per frame would be a waste
  // of a 60fps budget on a number that changes every five minutes.
  const [, bump] = useState(0);
  useEffect(() => {
    const t = setInterval(() => bump((v) => v + 1), 4000);
    return () => clearInterval(t);
  }, []);

  const now = serverNow();
  return (
    <group>
      {drawn.map((p) => {
        const g = p.item.kind === KIND.GROWER
          ? growthOf(p.item, holding.growers?.[p.propId], now)
          : null;
        return (
          <group key={p.propId}>
            <Placed
              propId={p.propId} tapRef={tapRef} hidden={movingPropId === p.propId}
              itemId={p.itemId} x={p.ax} z={p.az} rot={p.arot} lift={p.lift}
              fill={g?.fill || 0} ripe={!!g && g.cycles >= 1} selected={buildMode && selectedId === p.propId}
            />
            {g && g.ready > 0 && Math.hypot(p.ax - nowRef.current.x, p.az - nowRef.current.y) < LABEL_RADIUS && (
              // keyed on its spot: the mark sets its position once, so a moved
              // grower needs a fresh mark (E2E review)
              <ReadyMark key={`${p.ax},${p.az}`} x={p.ax} z={p.az + 0} amount={g.ready} />
            )}
          </group>
        );
      })}
    </group>
  );
}

// ── THE GHOST — what you are about to put down ──────────────────────
// It follows the player rather than the mouse. A pointer-raycast ghost
// is the obvious build, and it fights the camera the instant somebody
// orbits; "it goes where you are standing, facing where you are facing"
// needs no raycast, works identically on a phone, and cannot disagree
// with the collision test, which is also done in tile space.
// ⚠ `footprint` IS NOT DECORATION. A one-tile marker under a two-tile
// piece turns red for reasons entirely off-screen, and the player
// shuffles around hunting for a spot that was never the problem. Six
// items in the catalogue are footprint 2 and `collides()` has always
// been told the real number — only the ghost was lying.
// ⭐ (2026-09-15) It stands where the SCREEN says it stands — `ghostAtRef`
// is the engine's `reachTile()` or the tile you tapped — so the ring, the
// prompt and the collision test are one number. Three colours now: green
// for the ground, gold for "it balances on that", red for no.
function Ghost({ itemId, ghostAtRef, rot, state = "free" }) {
  const [sw, sd] = sizeOf(itemId);
  const fp = Math.max(sw, sd);
  const g = useRef();
  // starts where it will stand — not at the world origin, gliding in
  const init = useRef([ghostAtRef?.current?.x ?? 0, 0, ghostAtRef?.current?.y ?? 0]);
  useFrame(() => {
    const o = g.current; if (!o) return;
    const at = ghostAtRef?.current; if (!at) return;
    // glide, so a tapped aim slides over rather than teleporting — but a
    // long way (a fresh aim across the yard) snaps
    if (Math.hypot(at.x - o.position.x, at.y - o.position.z) > 4) { o.position.x = at.x; o.position.z = at.y; return; }
    o.position.x += (at.x - o.position.x) * 0.35;
    o.position.z += (at.y - o.position.z) * 0.35;
  });
  if (!itemId) return null;
  const tint = (GHOST_VERB[state] || GHOST_VERB.free).tint;
  return (
    <group ref={g} position={init.current}>
      <group rotation={[0, rot || 0, 0]}>
        <MineriumProp itemId={itemId} fill={0.5} />
        {/* a long piece shows the rectangle it will claim, turned with it */}
        {sw !== sd && <RectRing w={sw} d={sd} color={tint} opacity={0.9} y={0.04} />}
        {sw !== sd && (
          <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[sw, sd]} />
            <meshBasicMaterial color={tint} transparent opacity={0.14} toneMapped={false} depthWrite={false} />
          </mesh>
        )}
      </group>
      {sw === sd && (
        <mesh position={[0, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.44 * fp, 0.56 * fp, 26]} />
          <meshBasicMaterial color={tint} transparent opacity={0.9} toneMapped={false} depthWrite={false} />
        </mesh>
      )}
      {sw === sd && (
        <mesh position={[0, 0.03, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.44 * fp, 26]} />
          <meshBasicMaterial color={tint} transparent opacity={0.14} toneMapped={false} depthWrite={false} />
        </mesh>
      )}
    </group>
  );
}

// ── THE GRID — build mode only ──────────────────────────────────────
// A faint tile grid over the yard, so "where does this go" has an answer
// before the ghost turns red. One line geometry, no per-frame cost.
function TileGrid() {
  const geo = useMemo(() => {
    const pts = [];
    const x0 = YARD.x0 - 0.5, x1 = YARD.x1 + 0.5, z0 = YARD.y0 - 0.5, z1 = YARD.y1 + 0.5;
    for (let x = x0; x <= x1 + 1e-6; x += 1) pts.push(x, 0, z0, x, 0, z1);
    for (let z = z0; z <= z1 + 1e-6; z += 1) pts.push(x0, 0, z, x1, 0, z);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
    return g;
  }, []);
  useEffect(() => () => geo.dispose(), [geo]);
  return (
    <lineSegments geometry={geo} position={[0, 0.025, 0]}>
      <lineBasicMaterial color={UI.gold} transparent opacity={0.16} depthWrite={false} toneMapped={false} />
    </lineSegments>
  );
}

// ── THE BURSTS — +✹N rising off a collected grower ─────────────────
function Bursts({ bursts }) {
  return bursts.map((b) => (
    <group key={b.id} position={[b.x, 1.1, b.y]}>
      <Html center distanceFactor={DF_TILE} zIndexRange={[9, 0]} style={{ pointerEvents: "none" }}>
        <div style={{ position: "relative", width: 1, height: 1 }}>
          <div style={burstStyle}>+✹{(b.amount || 0).toLocaleString()}</div>
        </div>
      </Html>
    </group>
  ));
}

// ── THE DOOR MARK — where the Hall is ───────────────────────────────
function HouseDoorGlow({ posRef }) {
  const g = useRef();
  const [near, setNear] = useState(false);
  const acc = useRef(0);
  useFrame((_, dt) => {
    acc.current += dt || 0.016;
    if (acc.current < 0.2) return;
    acc.current = 0;
    const p = posRef.current;
    const d = Math.hypot(p.x - HOUSE_DOOR.x, p.y - HOUSE_DOOR.y) < 2.2;
    if (d !== near) setNear(d);
  });
  useFrame(({ clock }) => {
    if (g.current) g.current.scale.setScalar(1 + Math.sin(clock.elapsedTime * 2) * 0.06);
  });
  return (
    <group position={[HOUSE_DOOR.x, 0.12, HOUSE_DOOR.y]} ref={g}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.7, 0.86, 26]} />
        <meshBasicMaterial color={near ? C.omnis : "#6a5a30"} transparent opacity={near ? 0.9 : 0.4} toneMapped={false} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// ⭐ THE CAMERA RIG — THE CAMERA CONTRACT, all four pieces
// ────────────────────────────────────────────────────────────────────
// Identical to the pier's, and deliberately so: `pierCamera.js` stores
// the view as a distance MULTIPLIER rather than a position, which is
// exactly what lets ONE persisted setting be legal in a room, a plaza,
// a boardwalk and a yard at once. Each scene keeps the framing it was
// designed with, and "Follow" means 50% closer than whatever THAT
// scene already thought was right. The player picks a camera once and
// never picks it again at another door.
// ════════════════════════════════════════════════════════════════════
const TMP_A = new THREE.Vector3();
const TMP_B = new THREE.Vector3();

function CameraRig({ posRef, buildMode }) {
  const { camera, gl } = useThree();
  // ⭐ THE BUILD CAMERA (2026-09-15): higher and steeper while the shed
  // is open, so the far fence and the tile you are aiming at are both
  // in frame. A yard is a thing you arrange from above. Read by ref in
  // the loop; the lerp does the rest.
  const buildRef = useRef(buildMode);
  buildRef.current = buildMode;
  const controls = useRef();
  const lastTarget = useRef(new THREE.Vector3(posRef.current.x, 1, posRef.current.y));
  const lastReset = useRef(resetToken());

  // BASE framing — this scene's own idea of right. A view MULTIPLIES it.
  // Slightly higher and further than the pier's, because a yard is a
  // place you arrange rather than walk down.
  // ⚑ THE ASPECT FIT — the pier's rule, so a phone sees the yard and not a
  // fence post. Portrait stands the lens further back, capped.
  const { size } = useThree();
  const fit = size.width < size.height ? Math.min(1.5, 0.78 * (size.height / size.width)) : 1;
  const BASE_DIST = 11.0 * fit, BASE_HEIGHT = 7.0 * fit;

  // ⚠ A yaw left over from here would silently rotate input in any 2D
  // view. Reset on unmount, always.
  useEffect(() => () => resetGroundForward(), []);

  useFrame(() => {
    const c = controls.current; if (!c) return;
    const p = posRef.current;
    const tx = p.x, ty = 1.0, tz = p.y;

    // 4 · PUBLISH THE GROUND-FORWARD, every frame. World z IS tile y.
    camera.getWorldDirection(TMP_A);
    TMP_A.y = 0;
    if (TMP_A.lengthSq() > 1e-6) { TMP_A.normalize(); setGroundForward(TMP_A.x, TMP_A.z); }

    // 3 · DEFAULT is an ACTION, driven by a COUNTER — a boolean makes
    //     the button silently dead in the most likely case.
    const tok = resetToken();
    if (tok !== lastReset.current) {
      lastReset.current = tok;
      const m = viewMult();
      camera.position.set(tx, ty + BASE_HEIGHT * m, tz + BASE_DIST * m);
      c.target.set(tx, ty, tz);
      lastTarget.current.set(tx, ty, tz);
      c.update();
      return;
    }

    if (isPosed()) {
      // 2 · FREE POSE — TRANSLATE the pose, never re-aim it.
      TMP_B.set(tx - lastTarget.current.x, ty - lastTarget.current.y, tz - lastTarget.current.z);
      camera.position.add(TMP_B);
      c.target.set(tx, ty, tz);
      lastTarget.current.set(tx, ty, tz);
      c.update();
      return;
    }

    // 1 · the steered framing. Goal-based, never command-based.
    const m = viewMult();
    const bh = buildRef.current ? 1.7 : 1, bd = buildRef.current ? 0.8 : 1;   // dials
    TMP_B.set(tx, ty + BASE_HEIGHT * m * bh, tz + BASE_DIST * m * bd);
    camera.position.lerp(TMP_B, 0.055);
    c.target.lerp(TMP_A.set(tx, ty, tz), 0.12);
    lastTarget.current.copy(c.target);
    c.update();
  });

  return (
    <OrbitControls
      ref={controls} makeDefault enablePan={false}
      onStart={() => setPosed(true)}
      minDistance={2.4} maxDistance={40}
      maxPolarAngle={Math.PI / 1.9}
      enableDamping dampingFactor={0.08}
      domElement={gl.domElement}
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// THE SCENE
// ════════════════════════════════════════════════════════════════════
function Scene({
  holding, posRef, facingRef, ghostAtRef, tapRef, bursts, gender, title,
  buildMode, ghostItemId, ghostRot, ghostState, selectedId, movingPropId,
  petRef, companionPosRef, roster, othersRef,
}) {
  // one sky spec for the dome, the reading, the tint and THE RISE (review)
  const skySpec = SKY_RECIPES[holding.sky] || SKY_RECIPES.sky_default;
  return (
    <>
      {/* ⚑ FIXED LIGHT COUNT. THREE. FOREVER. */}
      {/* ⚑ THE PIER'S RIG, NUMBER FOR NUMBER. Three lights, forever —
          and these are the three. The yard was left on the OLD values
          (ambient 0.52 #8ea3d0, key 0.86) which is exactly the cold-fill,
          dim-key combination the pier's own margin note calls "mud": a
          40% dimmer key and a blue cast on warm ground, one door away
          from a boardwalk that had just been re-lit. The single most
          visible thing still separating the two rooms, and it is three
          numbers. Any change here belongs in both files, which is why
          they now read identically. */}
      {/* ⚑ STILL THE PIER'S RIG, NUMBER FOR NUMBER — updated with it on
          2026-09-13, when the boardwalk's ambient became a hemisphere
          (still three lights: sky from above, warm bounce from below). */}
      <hemisphereLight args={["#a89cb8", "#6a4a2c", 0.62]} />
      <directionalLight position={[18, 26, 12]} intensity={1.15} color="#ffdca4" />
      <directionalLight position={[-22, 14, -18]} intensity={0.42} color={C.nullus} />

      {/* ⭐ THE DOME AND ITS TINT — the four purchasable skies. */}
      <MineriumSky skyId={holding.sky} />
      {/* ⭐ AND THE STANDING HORIZON ITSELF, the same one the boardwalk
          has: shattered pocket dimensions, the Lyt/Lyf pairs, the ocean
          of Chao and THE RISE. Sized off this yard's own bounds, so the
          same code fills a 24×18 field and a 56-tile pier with no number
          changing. Zero new lights, `fog={false}` throughout. */}
      {/* ⚑ AND THE SKY YOU BOUGHT STILL BUYS SOMETHING. `tint` colours
          the cracked eclipse and `rise` switches THE RISE, so the four
          shelves differ by more than a gradient — which is the whole
          reason a Mythical sky has a price on it. */}
      {/* ⭐ (2026-09-16) and each sky is a different READING of it — see SKY_RECIPES */}
      <UnstableWorld3D cx={CX} cz={CZ} R={RAD * 1.6}
                       reading={skySpec.reading}
                       tint={skySpec.reading ? null : (skySpec.eclipse?.tint || null)}
                       rise={skySpec.reading ? true : skySpec.rise !== false}
                       hd={skySpec.hd || null} />
      <Yard groundId={holding.ground} tapRef={tapRef} />
      {buildMode && <TileGrid />}
      {/* ⭐ the house is built from the pier's own kit now — see mineriumManor.jsx */}
      <Manor cx={CX} front={YARD.y0 - 1.7} title={title} />
      <Fence />
      <HouseDoorGlow posRef={posRef} />

      <PropsLayer holding={holding} nowRef={posRef} selectedId={selectedId} buildMode={buildMode}
                  tapRef={tapRef} movingPropId={movingPropId} />
      <Bursts bursts={bursts || []} />

      {buildMode && ghostItemId && (
        <Ghost key={ghostItemId} itemId={ghostItemId} ghostAtRef={ghostAtRef} rot={ghostRot} state={ghostState} />
      )}

      <YardActor posRef={posRef} facingRef={facingRef} gender={gender} />
      {(roster || []).map((o) => <RemoteYardActor key={o.uid} uid={o.uid} meta={o} othersRef={othersRef} />)}
      {holding.companion?.beingId && (
        <Companion beingId={holding.companion.beingId} tier={holding.companion.tier} posRef={posRef}
                   petRef={petRef} outRef={companionPosRef} />
      )}

      <CameraRig posRef={posRef} buildMode={buildMode} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE HOST
// ────────────────────────────────────────────────────────────────────
// ⚠ React.memo, and it is the single most important line in the file.
// The screen above re-renders on every HUD tick; without this bail the
// whole 3D tree — sky, ground, every prop and the companion — would
// reconcile at that rate against a 60fps loop. The bail is SAFE because
// every live value reaches the scene through a REF the frame loop
// reads, which React neither sees nor needs to.
// ════════════════════════════════════════════════════════════════════
function Minerium3DImpl({
  holding, posRef, facingRef, ghostAtRef, tapRef, bursts, gender, title,
  buildMode, ghostItemId, ghostRot, ghostState, selectedId, movingPropId,
  petRef, companionPosRef, othersRef, rosterKey = "",
}) {
  const [, force] = useState(0);
  useEffect(() => subscribe(() => force((v) => v + 1)), []);
  // who is here travels as a KEY (arrivals/departures); where they are, by ref
  const roster = useMemo(() => Object.entries(othersRef?.current || {})
    .filter(([, d]) => !!d).slice(0, 8)
    .map(([uid, d]) => ({ uid, gender: d.gender, name: d.name })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [rosterKey, othersRef]);
  // the pier kit's cached textures let go of this renderer on unmount (pierTex)
  // ⚠ AND THE BEING KIT'S (2026-09-15): the sky dome, the ground and the
  // props' water come from `ieBeingModels.canvasTex`, whose cache is not
  // pierTex's — those textures kept every old renderer alive through their
  // dispose listeners. Disposing frees the GPU copy only; the next
  // renderer re-uploads from the same canvas.
  useEffect(() => () => {
    releaseTextures(); releasePropMaterials();
    for (const t of BEING_TEX.values()) { try { t.dispose(); } catch { /* gone */ } }
  }, []);

  const view = getView();
  const posed = isPosed();

  return (
    <div style={{ position: "absolute", inset: 0, background: "#04030a" }}>
      <Canvas
        dpr={[1, 1.5]}
        shadows={false}
        // near/far matched to the pier's for the same depth-precision reason
        camera={{ fov: 55, near: 0.25, far: 1600, position: [YARD.spawn.x, 7, YARD.spawn.y + 11] }}
        gl={{ antialias: true, powerPreference: "high-performance" }}
        onCreated={({ gl }) => { gl.setClearColor("#04030a"); }}
      >
        <Scene
          holding={holding} posRef={posRef} facingRef={facingRef} ghostAtRef={ghostAtRef} tapRef={tapRef}
          bursts={bursts} gender={gender} title={title}
          buildMode={buildMode} ghostItemId={ghostItemId} ghostRot={ghostRot}
          ghostState={ghostState} selectedId={selectedId} movingPropId={movingPropId}
          petRef={petRef} companionPosRef={companionPosRef} roster={roster} othersRef={othersRef}
        />
      </Canvas>

      {/* ── THE CAMERA BAR ────────────────────────────────────────────
          ⚠ A CONTROL NOBODY CAN SEE IS A CONTROL THAT ISN'T THERE. Same
          corner, every room — this one matches the pier's exactly so a
          player never hunts for it after walking through a door. */}
      <CameraBar view={view} posed={posed} force={force} />
    </div>
  );
}

// ⚑ BYTE-FOR-BYTE THE PIER'S CONTROL. This file's own margin already
// promised it — "this one matches the pier's exactly so a player never
// hunts for it after walking through a door" — beside a copy that was
// lime where the pier's is gold. Now it is the shared helper, so the
// promise is structural instead of a comment.

export default React.memo(Minerium3DImpl);
